import React, { useState, useEffect } from 'react';
import { Calculator, TrendingUp, DollarSign, Calendar, PiggyBank, Percent } from 'lucide-react';
import { formatCurrency, formatNumber } from '../lib/formatters';

export const ROICalculator = ({ 
  sharePrice = 1000, 
  avgWeeklyProfit = 0, 
  totalShares = 100,
  assetName = "Asset"
}) => {
  const [investmentAmount, setInvestmentAmount] = useState(5000);
  const [projections, setProjections] = useState(null);

  useEffect(() => {
    calculateProjections();
  }, [investmentAmount, sharePrice, avgWeeklyProfit]);

  const calculateProjections = () => {
    if (sharePrice <= 0 || avgWeeklyProfit <= 0) {
      setProjections(null);
      return;
    }

    const sharesYouCanBuy = Math.floor(investmentAmount / sharePrice);
    const actualInvestment = sharesYouCanBuy * sharePrice;
    
    // Calculate profit per share per week
    const profitPerSharePerWeek = avgWeeklyProfit / totalShares;
    
    // Weekly return for your shares
    const weeklyReturn = sharesYouCanBuy * profitPerSharePerWeek;
    const monthlyReturn = weeklyReturn * 4.33; // avg weeks per month
    const yearlyReturn = weeklyReturn * 52;
    
    // ROI percentages
    const yearlyROI = (yearlyReturn / actualInvestment) * 100;
    const monthlyROI = yearlyROI / 12;
    
    // Time to break even
    const weeksToBreakEven = actualInvestment / weeklyReturn;
    const monthsToBreakEven = weeksToBreakEven / 4.33;

    setProjections({
      sharesYouCanBuy,
      actualInvestment,
      profitPerSharePerWeek,
      weeklyReturn,
      monthlyReturn,
      yearlyReturn,
      yearlyROI,
      monthlyROI,
      weeksToBreakEven,
      monthsToBreakEven,
    });
  };

  const presetAmounts = [1000, 2500, 5000, 10000, 25000];

  return (
    <div className="bg-white border border-[hsl(var(--border))] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[hsl(var(--brand-primary))] to-[hsl(var(--brand-primary-dark))] p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Calculator className="w-6 h-6" />
          <h3 className="text-xl font-bold">ROI Calculator</h3>
        </div>
        <p className="text-white/80 text-sm">
          See your potential returns based on historical performance
        </p>
      </div>

      <div className="p-6">
        {/* Investment Input */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-[hsl(var(--text-secondary))] mb-2">
            How much do you want to invest?
          </label>
          <div className="relative">
            <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--text-tertiary))]" />
            <input
              type="number"
              data-testid="roi-investment-input"
              value={investmentAmount}
              onChange={(e) => setInvestmentAmount(Math.max(0, parseInt(e.target.value) || 0))}
              className="w-full pl-12 pr-4 py-4 text-2xl font-mono font-bold border border-[hsl(var(--border))] rounded-xl focus:ring-2 focus:ring-[hsl(var(--brand-primary))] focus:border-transparent"
              placeholder="5000"
            />
          </div>
          
          {/* Preset Buttons */}
          <div className="flex flex-wrap gap-2 mt-3">
            {presetAmounts.map((amount) => (
              <button
                key={amount}
                onClick={() => setInvestmentAmount(amount)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  investmentAmount === amount
                    ? 'bg-[hsl(var(--brand-primary))] text-white'
                    : 'bg-[hsl(var(--background-secondary))] text-[hsl(var(--text-secondary))] hover:bg-[hsl(var(--border))]'
                }`}
              >
                ${formatNumber(amount)}
              </button>
            ))}
          </div>
        </div>

        {/* Current Data Info */}
        <div className="bg-[hsl(var(--background-secondary))] rounded-xl p-4 mb-6">
          <div className="text-sm text-[hsl(var(--text-secondary))] mb-2">Based on historical data:</div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="text-xs text-[hsl(var(--text-tertiary))]">Share Price</div>
              <div className="text-lg font-bold font-mono text-[hsl(var(--text-primary))]">{formatCurrency(sharePrice)}</div>
            </div>
            <div>
              <div className="text-xs text-[hsl(var(--text-tertiary))]">Avg Weekly Profit</div>
              <div className="text-lg font-bold font-mono text-green-600">{formatCurrency(avgWeeklyProfit)}</div>
            </div>
          </div>
        </div>

        {/* Projections */}
        {projections && projections.sharesYouCanBuy > 0 ? (
          <div className="space-y-4">
            {/* Shares You Can Buy */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-blue-700">Shares You Can Buy</div>
                  <div className="text-3xl font-bold text-blue-800 font-mono">
                    {formatNumber(projections.sharesYouCanBuy)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-blue-700">Actual Investment</div>
                  <div className="text-xl font-bold text-blue-800 font-mono">
                    {formatCurrency(projections.actualInvestment)}
                  </div>
                </div>
              </div>
            </div>

            {/* Projected Returns */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <Calendar className="w-5 h-5 text-green-600 mx-auto mb-1" />
                <div className="text-xs text-green-700 uppercase tracking-wide">Weekly Return</div>
                <div className="text-2xl font-bold text-green-800 font-mono">
                  {formatCurrency(projections.weeklyReturn)}
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <TrendingUp className="w-5 h-5 text-green-600 mx-auto mb-1" />
                <div className="text-xs text-green-700 uppercase tracking-wide">Monthly Return</div>
                <div className="text-2xl font-bold text-green-800 font-mono">
                  {formatCurrency(projections.monthlyReturn)}
                </div>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                <PiggyBank className="w-5 h-5 text-green-600 mx-auto mb-1" />
                <div className="text-xs text-green-700 uppercase tracking-wide">Yearly Return</div>
                <div className="text-2xl font-bold text-green-800 font-mono">
                  {formatCurrency(projections.yearlyReturn)}
                </div>
              </div>
            </div>

            {/* ROI & Break Even */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Percent className="w-5 h-5 text-purple-600" />
                  <span className="text-sm text-purple-700">Annual ROI</span>
                </div>
                <div className="text-3xl font-bold text-purple-800 font-mono">
                  {projections.yearlyROI.toFixed(1)}%
                </div>
                <div className="text-xs text-purple-600 mt-1">
                  {projections.monthlyROI.toFixed(2)}% per month
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-amber-600" />
                  <span className="text-sm text-amber-700">Break Even</span>
                </div>
                <div className="text-3xl font-bold text-amber-800 font-mono">
                  {projections.monthsToBreakEven.toFixed(1)}
                </div>
                <div className="text-xs text-amber-600 mt-1">
                  months (~{Math.ceil(projections.weeksToBreakEven)} weeks)
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="bg-gray-50 rounded-lg p-3 text-xs text-gray-500">
              <strong>Disclaimer:</strong> These projections are based on historical performance data and are not guaranteed. 
              Actual returns may vary based on market conditions, operational factors, and other variables.
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-[hsl(var(--text-secondary))]">
            {avgWeeklyProfit <= 0 ? (
              <p>No performance data available yet for ROI calculation.</p>
            ) : (
              <p>Enter an investment amount to see projected returns.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ROICalculator;
